/**
 * Relative timestamps for Play Updates / relationship activity (locale-aware).
 * Scale: Just now → N minutes ago → Yesterday → weekday → month day → full date.
 */

export function formatPlayActivityRelativeTime(
  timestamp: Date | string | number,
  now: Date = new Date(),
  locale?: string
): string {
  const time =
    typeof timestamp === 'number'
      ? timestamp
      : typeof timestamp === 'string'
        ? new Date(timestamp).getTime()
        : timestamp.getTime()
  if (!Number.isFinite(time) || time <= 0) return ''

  const diffMs = now.getTime() - time
  if (diffMs < 0) return 'Just now'

  const diffSeconds = Math.floor(diffMs / 1000)
  if (diffSeconds < 45) return 'Just now'

  const diffMinutes = Math.floor(diffSeconds / 60)
  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`
  }

  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
  }

  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  const startOfTarget = new Date(time)
  startOfTarget.setHours(0, 0, 0, 0)
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfTarget.getTime()) / (24 * 60 * 60 * 1000)
  )

  if (dayDiff === 1) return 'Yesterday'

  if (dayDiff > 1 && dayDiff < 7) {
    return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(new Date(time))
  }

  const targetYear = new Date(time).getFullYear()
  if (targetYear === now.getFullYear()) {
    return new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(
      new Date(time)
    )
  }

  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(time))
}
